import React, { useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import {
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	XAxis,
	YAxis,
	Tooltip,
	Bar,
	LabelList,
} from "recharts";
import { Dispatch } from "redux";
import reportSelectors from "redux/app/report/selectors";
import { IStore } from "redux/store";
import {
	isCargoSlopDataEmpty,
	transformCargoSlopRawData,
	transformVesselSpecificCargoSlopRawData,
} from "redux/app/report/utils";
import CustomTooltip from "../../../common/CustomTooltip";
// import CustomTooltip from "./CustomTooltip";

const dischargeMethods = [
	{
		name: "Total discharged",
		key: "totalDischarged",
		stackId: "a",
		color: "#05a3e0",
	},
];

const xAxisScale = "Quarter";
const yAxisScale = "m³";

type OwnProps = {
	reportContainerWidth: number;
};
type GraphProps = PropsFromRedux & OwnProps;
function Graph(props: GraphProps) {
	const {
		reportContainerWidth,
		vesselId,
		year,
		rawCargoSlopData,
		vesselSpecificRawCargoSlopData,
	} = props;

	const allVesselfinalData = useMemo(() => {
		return vesselId ? [] : transformCargoSlopRawData(rawCargoSlopData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rawCargoSlopData, year, vesselId]);

	const vesselSpecificFinalData = useMemo(() => {
		return vesselId
			? transformVesselSpecificCargoSlopRawData(vesselSpecificRawCargoSlopData)
			: [];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vesselSpecificRawCargoSlopData, vesselId, year]);

	const finalData = vesselId ? vesselSpecificFinalData : allVesselfinalData;

	const bars = [...dischargeMethods].reverse();

	return (
		<div className="flex justify-center m-2">
			<div className="p-4 px-6 shadow-md max-w-4xl rounded-md border border-gray-300">
				<h3 className="font-medium text-center py-1.5">Cargo Slop (m³)</h3>
				<div className="flex flex-wrap p-4 pb-1.5">
					{dischargeMethods.map((category) => {
						const { name, color } = category;
						return (
							<div
								key={name}
								className="flex  md:min-w-[180px] lg:min-w-[150px] xl:min-w-[170px] basis-1/3  lg:text-[12px] xl:text-sm space-x-1.5 py-0.5 items-center pl-1.5"
							>
								<span
									style={{ background: color }}
									className="w-4 h-4 rounded-full"
								/>
								<div>
									<span>{name}</span>
								</div>
							</div>
						);
					})}
				</div>
				<div className="axis flex justify-end text-[#7E7E7E] mt-4">
					<p className="m-0 text-[12px] font-medium px-3.5 border-r-2 border-[#7E7E7E]">
						X-axis - {xAxisScale}
					</p>
					<p className="m-0 text-[12px] font-medium px-3.5">
						Y-axis - {yAxisScale}
					</p>
				</div>
				<div className="flex justify-center">
					<ResponsiveContainer
						width={
							reportContainerWidth < 800
								? 0.7 * reportContainerWidth
								: (reportContainerWidth - 75) / 2
						}
						height={375}
					>
						<BarChart
							width={
								reportContainerWidth < 800
									? 0.7 * reportContainerWidth
									: (reportContainerWidth - 75) / 2
							}
							data={finalData}
							margin={{
								top: 25,
								right: 10,
								left: -20,
								bottom: 5,
							}}
							barGap={0}
						>
							<CartesianGrid vertical={false} fill="" />
							<XAxis tickLine={false} dataKey="quarter" fontSize={14} />
							<YAxis tickLine={false} fontSize={14} />
							<Tooltip content={<CustomTooltip legends={dischargeMethods} />} />
							{bars.map((discharge, index) => {
								const { color, key, stackId, name } = discharge;

								return (
									<Bar
										barSize={30}
										key={name}
										dataKey={key}
										stackId={stackId}
										fill={color}
									>
										{index === dischargeMethods.length - 1 && (
											<LabelList
												className="text-[12px]"
												dataKey={"total"}
												position="top"
											/>
										)}
									</Bar>
								);
							})}
						</BarChart>
					</ResponsiveContainer>
				</div>
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const reportStore = reportSelectors._getStore(store);
	const vesselId = reportSelectors.getGenericVesselId(reportStore);
	const year = reportSelectors.getGenericYear(reportStore);
	const rawCargoSlopData = reportSelectors.getRawCargoSlopData(reportStore);
	const vesselSpecificRawCargoSlopData =
		reportSelectors.getRawVesselSpecificCargoSlopData(reportStore);

	return {
		vesselId,
		year,
		rawCargoSlopData,
		vesselSpecificRawCargoSlopData,
	};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(Graph);
